/**
 * Stage 26 · Circularity & Sustainability Evidence.
 * Evidence-only. Never claims CO2 unless a registered methodology exists.
 */
import { orgLabel } from "@/lib/orgLabel";
import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Recycle, GitBranch, Beaker, Search, Plus, Mail } from "lucide-react";

const LADDER = ["extend_life","repair","reuse","redeploy","resell_buyback","recycle","destroy"];
const LADDER_LABELS = {
  extend_life: "Extend life", repair: "Repair", reuse: "Reuse", redeploy: "Redeploy",
  resell_buyback: "Resell / Buyback", recycle: "Recycle", destroy: "Destroy",
};

export default function Circularity() {
  const [tab, setTab] = useState("overview");
  const [masters, setMasters] = useState([]); const [mc, setMc] = useState(""); const [me, setMe] = useState(null);
  useEffect(() => {
    api.get("/auth/me").then(({ data }) => { setMe(data); if (data?.master_company_id) setMc(data.master_company_id); });
    api.get("/master-companies").then(({ data }) => { setMasters(data.items || []); setMc(current => current || data.items?.[0]?.id || ""); });
  }, []);
  const isSuntronix = me && !me.master_company_id;
  return (
    <div className="max-w-6xl space-y-5" data-testid="circularity">
      <div>
        <div className="text-[10px] font-semibold tracking-[0.14em] uppercase text-slate-500">Insights · Stage 26</div>
        <h1 className="text-3xl font-heading font-semibold text-slate-900 tracking-tight mt-1">ONE Circularity</h1>
        <p className="text-sm text-slate-600 mt-2 max-w-2xl">
          Evidence, not slogans. Extend life · repair · reuse · redeploy · resell · recycle · destroy — every event grounded in a platform record. Environmental impact only appears when a registered methodology captures its inputs.
        </p>
      </div>
      {isSuntronix && (
        <select className="input min-w-[240px]" value={mc} onChange={(e) => setMc(e.target.value)} data-testid="cir-tenant">
          {masters.map((m) => <option key={m.id} value={m.id}>{orgLabel(m, masters)}</option>)}
        </select>
      )}
      <nav className="flex flex-wrap gap-1 border-b border-slate-200" role="tablist">
        {[
          { key: "overview", label: "Overview",         icon: Recycle },
          { key: "360",      label: "Circularity 360",  icon: GitBranch },
          { key: "log",      label: "Log event",        icon: Plus },
          { key: "method",   label: "Methodologies",    icon: Beaker },
          { key: "subs",     label: "Auto-email",       icon: Mail },
        ].map(({ key, label, icon: Icon }) => (
          <button key={key} onClick={() => setTab(key)} data-testid={`cir-tab-${key}`}
            className={"px-3 py-2 text-sm inline-flex items-center gap-1.5 border-b-2 -mb-px " +
              (tab === key ? "border-slate-900 text-slate-900 font-medium" : "border-transparent text-slate-500 hover:text-slate-800")}>
            <Icon className="h-4 w-4" /> {label}
          </button>
        ))}
      </nav>
      {mc && (
        <>
          {tab === "overview" && <Overview mc={mc} />}
          {tab === "360"      && <Circle360 mc={mc} />}
          {tab === "log"      && <LogEvent mc={mc} />}
          {tab === "method"   && <Methodologies isSuntronix={isSuntronix} mc={mc} />}
          {tab === "subs"     && <Subscriptions mc={mc} />}
        </>
      )}
    </div>
  );
}

function Overview({ mc }) {
  const [m, setM] = useState(null);
  useEffect(() => { api.get("/circularity/metrics", { params: { master_company_id: mc, days: 365 } }).then(({ data }) => setM(data)); }, [mc]);
  const downloadPdf = async () => {
    try {
      const res = await api.get(`/circularity/statement/${mc}/pdf`, { params: { days: 30 }, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `sustainability-statement-${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Sustainability statement downloaded");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail || "PDF failed")); }
  };
  if (!m) return <div className="text-sm text-slate-500">Loading…</div>;
  const total = m.total_disposition_events || 1;
  return (
    <div className="space-y-4" data-testid="cir-overview">
      <div className="flex justify-end">
        <button onClick={downloadPdf}
          className="h-9 px-3 text-sm rounded-md border border-emerald-200 text-emerald-800 bg-emerald-50 hover:bg-emerald-100 inline-flex items-center gap-1.5"
          data-testid="cir-download-pdf">
          Download sustainability statement (PDF · 30d)
        </button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Tile label="Redeployed" value={m.counts_by_disposition.redeploy} />
        <Tile label="Reused"      value={m.counts_by_disposition.reuse} />
        <Tile label="Repaired"    value={m.counts_by_disposition.repair} />
        <Tile label="Resold"      value={m.counts_by_disposition.resell_buyback} />
        <Tile label="Recycled"    value={m.counts_by_disposition.recycle} />
        <Tile label="Destroyed"   value={m.counts_by_disposition.destroy} />
        <Tile label="Avg useful life" value={m.avg_useful_life_months != null ? `${m.avg_useful_life_months} mo` : "—"} />
        <Tile label="Buyback (INR)" value={"₹" + Number(m.buyback_recovery_inr || 0).toLocaleString("en-IN")} tone="ok" />
      </div>
      <section className="bg-white border border-slate-200 rounded-md p-4">
        <h3 className="text-sm font-medium text-slate-900 mb-2">Circularity ladder</h3>
        <ol className="space-y-1">
          {LADDER.map((d, i) => {
            const v = m.counts_by_disposition[d] || 0;
            const pct = Math.round(100 * v / total);
            return (
              <li key={d} className="text-xs flex items-center gap-2">
                <span className="w-6 text-slate-400 tabular-nums">{i + 1}.</span>
                <span className="w-32 text-slate-700">{LADDER_LABELS[d]}</span>
                <div className="flex-1 h-2 bg-slate-100 rounded overflow-hidden">
                  <div className="h-full bg-emerald-500" style={{ width: pct + "%" }} />
                </div>
                <span className="w-10 text-right tabular-nums text-slate-700">{v}</span>
              </li>
            );
          })}
        </ol>
        <div className="mt-3 text-[10px] uppercase tracking-widest text-slate-500">{(m.labels || []).join(" · ")}</div>
      </section>
    </div>
  );
}

function Circle360({ mc }) {
  const [asset, setAsset] = useState(""); const [data, setData] = useState(null);
  const run = async () => {
    if (!asset) return;
    try { const { data } = await api.get(`/circularity/360/${asset}`); setData(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setData(null); }
  };
  return (
    <div className="space-y-3" data-testid="cir-360">
      <div className="flex items-center gap-2">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input className="input pl-9 w-full" placeholder="Asset ID" value={asset} onChange={(e) => setAsset(e.target.value)} data-testid="cir-asset-input" />
        </div>
        <button onClick={run} className="h-9 px-3 text-sm rounded-md bg-slate-900 text-white" data-testid="cir-run">View 360</button>
      </div>
      {data && (
        <div className="space-y-3">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Tile label="Redeployments" value={data.totals.redeployments} />
            <Tile label="Repairs"       value={data.totals.repairs} />
            <Tile label="Resells"       value={data.totals.resells} />
            <Tile label="Lifetime"      value={data.totals.lifetime_days != null ? `${Math.round(data.totals.lifetime_days / 30)} mo` : "—"} />
            <Tile label="Buyback (INR)" value={"₹" + Number(data.totals.buyback_proceeds_inr || 0).toLocaleString("en-IN")} tone="ok" />
            <Tile label="Final disposition" value={data.totals.final_disposition || "—"} />
          </div>
          <section className="bg-white border border-slate-200 rounded-md">
            <div className="p-3 border-b border-slate-200 text-sm font-medium text-slate-900">Timeline</div>
            <div className="divide-y">
              {(data.timeline || []).length === 0 ? (
                <div className="p-4 text-sm text-slate-500">No timeline events recorded for this asset.</div>
              ) : data.timeline.map((t, i) => (
                <div key={i} className="p-3 flex items-center gap-3 text-sm">
                  <span className="text-[10px] font-mono w-40 tabular-nums text-slate-500">{(t.at || "").slice(0, 19).replace("T", " ")}</span>
                  <span className={"text-[10px] font-semibold uppercase tracking-widest px-1.5 py-0.5 rounded border " + kindChip(t.kind)}>{t.kind}</span>
                  <span className="text-slate-800">{t.label}</span>
                  {t.grade && <span className="text-xs text-slate-500 ml-auto">grade {t.grade}</span>}
                  {t.proceeds_inr && <span className="text-xs text-emerald-700 ml-2">₹{Number(t.proceeds_inr).toLocaleString("en-IN")}</span>}
                </div>
              ))}
            </div>
          </section>
          <div className="text-[10px] uppercase tracking-widest text-slate-500">{data.note}</div>
        </div>
      )}
    </div>
  );
}
function kindChip(k) {
  return k === "disposition" ? "bg-emerald-100 text-emerald-800 border-emerald-200" :
         k === "wipe"        ? "bg-sky-100 text-sky-800 border-sky-200" :
         k === "itad"        ? "bg-orange-100 text-orange-800 border-orange-200" :
         "bg-slate-100 text-slate-700 border-slate-200";
}

function LogEvent({ mc }) {
  const [f, setF] = useState({ asset_id: "", disposition: "redeploy", grade: "B", reason: "", partner_id: "", country: "IN", proceeds_inr: 0, weight_kg: 0, material_notes: "" });
  const save = async () => {
    try {
      await api.post("/circularity/events", { master_company_id: mc, ...f, proceeds_inr: Number(f.proceeds_inr) || null, weight_kg: Number(f.weight_kg) || null });
      toast.success("Disposition logged"); setF({ ...f, asset_id: "" });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="bg-white border border-slate-200 rounded-md p-3 space-y-2 max-w-2xl" data-testid="cir-log-form">
      <div className="grid grid-cols-2 gap-2">
        <label className="col-span-2"><div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Asset ID</div>
          <input className="input" value={f.asset_id} onChange={(e) => setF({ ...f, asset_id: e.target.value })} data-testid="cir-log-asset" /></label>
        <label><div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Disposition</div>
          <select className="input" value={f.disposition} onChange={(e) => setF({ ...f, disposition: e.target.value })} data-testid="cir-log-disposition">
            {LADDER.map((d) => <option key={d} value={d}>{LADDER_LABELS[d]}</option>)}
          </select></label>
        <label><div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Grade</div>
          <select className="input" value={f.grade} onChange={(e) => setF({ ...f, grade: e.target.value })}>
            {["A","B","C","D","scrap"].map((g) => <option key={g} value={g}>{g}</option>)}
          </select></label>
        <label><div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Country</div>
          <input className="input" value={f.country} onChange={(e) => setF({ ...f, country: e.target.value })} /></label>
        <label><div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Reason</div>
          <input className="input" value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} placeholder="warranty_expiry, age_policy…" /></label>
        <label><div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Proceeds (INR)</div>
          <input type="number" className="input" value={f.proceeds_inr} onChange={(e) => setF({ ...f, proceeds_inr: e.target.value })} /></label>
        <label><div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Weight (kg)</div>
          <input type="number" step="0.01" className="input" value={f.weight_kg} onChange={(e) => setF({ ...f, weight_kg: e.target.value })} /></label>
        <label className="col-span-2"><div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Material notes</div>
          <input className="input" value={f.material_notes} onChange={(e) => setF({ ...f, material_notes: e.target.value })} placeholder="Aluminium chassis · Li-ion battery removed" /></label>
      </div>
      <div className="flex justify-end pt-1">
        <button className="h-9 px-3 text-sm rounded-md bg-slate-900 text-white" onClick={save} data-testid="cir-log-save">Log disposition</button>
      </div>
    </div>
  );
}

function Methodologies({ isSuntronix, mc }) {
  const [items, setItems] = useState([]); const [showForm, setShowForm] = useState(false);
  const [applyResult, setApplyResult] = useState(null);
  const load = () => api.get("/circularity/methodologies").then(({ data }) => setItems(data.items || []));
  useEffect(() => { load().catch(() => {}); }, []);
  const apply = async (code) => {
    try { const { data } = await api.post(`/circularity/methodologies/${code}/apply?master_company_id=${mc}&days=365`); setApplyResult(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="space-y-3" data-testid="cir-methodologies">
      {isSuntronix && (
        <div className="flex justify-end">
          <button onClick={() => setShowForm(true)} className="h-9 px-3 text-sm rounded-md bg-slate-900 text-white inline-flex items-center gap-1" data-testid="cir-method-new">
            <Plus className="h-4 w-4" /> Register methodology
          </button>
        </div>
      )}
      {showForm && <MethodForm onCancel={() => setShowForm(false)} onCreated={() => { setShowForm(false); load(); }} />}
      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="text-[10px] uppercase tracking-widest text-slate-500 bg-slate-50">
            <tr><th className="text-left py-2 px-3">Code</th><th className="text-left py-2 px-3">Name</th><th className="text-left py-2 px-3">Source</th><th className="text-left py-2 px-3">Unit</th><th className="text-right py-2 px-3"></th></tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr><td colSpan={5} className="py-6 text-center text-slate-500">No methodologies registered. Environmental impact will not be shown.</td></tr>
            ) : items.map((m) => (
              <tr key={m.id} className="border-t border-slate-100">
                <td className="py-2 px-3 font-mono text-slate-900">{m.code}</td>
                <td className="py-2 px-3 text-slate-800">{m.name}</td>
                <td className="py-2 px-3 text-slate-500 text-xs">{m.source || "—"}</td>
                <td className="py-2 px-3 text-slate-600">{m.unit}</td>
                <td className="py-2 px-3 text-right">
                  <button onClick={() => apply(m.code)} className="text-xs px-2 py-0.5 rounded border border-slate-200 hover:bg-slate-50" data-testid={`cir-apply-${m.code}`}>Apply</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {applyResult && (
        <div className="bg-white border border-slate-200 rounded-md p-4" data-testid="cir-apply-result">
          <div className="text-sm font-medium text-slate-900">{applyResult.methodology.name} · v{applyResult.methodology.version}</div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-1">{applyResult.methodology.source || "no source cited"}</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
            <Tile label={`Impact (${applyResult.impact_unit})`} value={applyResult.impact_value ?? "—"} tone={applyResult.impact_value != null ? "ok" : undefined} />
            <Tile label="Devices used" value={applyResult.inputs.devices} />
            <Tile label="Weight used (kg)" value={applyResult.inputs.weight_kg} />
            <Tile label="Computed using" value={applyResult.computed_using || "—"} />
          </div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-3">{applyResult.note}</div>
        </div>
      )}
    </div>
  );
}
function MethodForm({ onCancel, onCreated }) {
  const [f, setF] = useState({ code: "", name: "", description: "", source: "", inputs_required: "", unit: "kg_co2e", factor_per_kg: 0, factor_per_device: 0, active: true });
  const save = async () => {
    try {
      await api.post("/circularity/methodologies", {
        ...f, inputs_required: f.inputs_required.split(",").map((x) => x.trim()).filter(Boolean),
        factor_per_kg: Number(f.factor_per_kg) || null, factor_per_device: Number(f.factor_per_device) || null,
      });
      toast.success("Methodology registered"); onCreated();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="bg-white border border-slate-200 rounded-md p-3 space-y-2" data-testid="cir-method-form">
      <div className="grid grid-cols-2 gap-2">
        <label><div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Code</div>
          <input className="input" value={f.code} onChange={(e) => setF({ ...f, code: e.target.value })} placeholder="co2_per_device_v1" data-testid="cir-method-code" /></label>
        <label><div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Name</div>
          <input className="input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="cir-method-name" /></label>
        <label className="col-span-2"><div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Source (paper / standard reference)</div>
          <input className="input" value={f.source} onChange={(e) => setF({ ...f, source: e.target.value })} placeholder="ISO 14040 · publication citation…" /></label>
        <label className="col-span-2"><div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Description</div>
          <textarea className="input min-h-[60px]" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="cir-method-desc" /></label>
        <label><div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Unit</div>
          <input className="input" value={f.unit} onChange={(e) => setF({ ...f, unit: e.target.value })} /></label>
        <label><div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Factor / kg</div>
          <input type="number" step="0.01" className="input" value={f.factor_per_kg} onChange={(e) => setF({ ...f, factor_per_kg: e.target.value })} /></label>
        <label><div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Factor / device</div>
          <input type="number" step="0.1" className="input" value={f.factor_per_device} onChange={(e) => setF({ ...f, factor_per_device: e.target.value })} /></label>
      </div>
      <div className="flex justify-end gap-2 pt-1">
        <button className="h-9 px-3 text-sm rounded-md border border-slate-200 bg-white" onClick={onCancel}>Cancel</button>
        <button className="h-9 px-3 text-sm rounded-md bg-slate-900 text-white" onClick={save} data-testid="cir-method-save">Register</button>
      </div>
    </div>
  );
}

function Tile({ label, value, tone }) {
  const cls = tone === "ok" ? "text-emerald-700" : tone === "warn" ? "text-amber-700" : "text-slate-900";
  return (
    <div className="rounded-md border border-slate-200 bg-white p-3">
      <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">{label}</div>
      <div className={"mt-1 text-xl font-heading font-semibold tabular-nums " + cls}>{value ?? "—"}</div>
    </div>
  );
}


function Subscriptions({ mc }) {
  const [sub, setSub] = useState(null);
  const [emails, setEmails] = useState("");
  const [day, setDay] = useState(1);
  const [active, setActive] = useState(true);
  const [period, setPeriod] = useState(30);
  const [dispatches, setDispatches] = useState([]);
  const load = useCallback(() => {
    api.get("/circularity/subscriptions", { params: { master_company_id: mc } }).then(({ data }) => {
      setSub(data);
      setEmails((data.recipient_emails || []).join(", "));
      setDay(data.day_of_month || 1);
      setActive(data.active ?? true);
      setPeriod(data.period_days || 30);
    });
    api.get(`/circularity/subscriptions/${mc}/dispatches`).then(({ data }) => setDispatches(data.items || [])).catch(() => setDispatches([]));
  }, [mc]);
  useEffect(() => { if (mc) load(); }, [mc, load]);
  const save = async () => {
    const recipient_emails = emails.split(/[,;\s]+/).map((s) => s.trim()).filter(Boolean);
    if (recipient_emails.length === 0) { toast.error("Add at least one recipient email"); return; }
    try {
      await api.post("/circularity/subscriptions", {
        master_company_id: mc, recipient_emails, day_of_month: Number(day) || 1,
        active, period_days: Number(period) || 30,
      });
      toast.success("Subscription saved"); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const sendNow = async () => {
    try {
      await api.post(`/circularity/subscriptions/${mc}/dispatch-now`);
      toast.success("Statement dispatched (MOCKED delivery — see history)");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="space-y-4" data-testid="cir-subs">
      <div className="bg-amber-50 border border-amber-200 text-amber-900 rounded-md p-3 text-xs">
        <b>MOCKED delivery notice.</b> Email transport is not yet configured — every dispatch is logged as an evidence-only record. Wire Resend/SendGrid in future to enable real inbox delivery.
      </div>
      <div className="bg-white border border-slate-200 rounded-md p-4 space-y-3">
        <div className="text-sm font-medium text-slate-900">Monthly sustainability statement</div>
        <label className="block">
          <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Recipients (comma-separated)</div>
          <input className="input w-full" value={emails} onChange={(e) => setEmails(e.target.value)} placeholder="cfo@customer.com, sustainability@customer.com" data-testid="cir-sub-emails" />
        </label>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <label>
            <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Day of month</div>
            <input type="number" min="1" max="28" className="input" value={day} onChange={(e) => setDay(e.target.value)} data-testid="cir-sub-day" />
          </label>
          <label>
            <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Period (days)</div>
            <input type="number" min="7" max="90" className="input" value={period} onChange={(e) => setPeriod(e.target.value)} data-testid="cir-sub-period" />
          </label>
          <label className="flex items-center gap-2 pt-6">
            <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} data-testid="cir-sub-active" />
            <span className="text-sm text-slate-700">Active</span>
          </label>
        </div>
        <div className="flex justify-end gap-2">
          <button onClick={sendNow} disabled={!sub?.active}
            className="h-9 px-3 text-sm rounded-md border border-emerald-300 text-emerald-800 bg-emerald-50 hover:bg-emerald-100 disabled:opacity-60"
            data-testid="cir-sub-send-now">Send now</button>
          <button onClick={save} className="h-9 px-3 text-sm rounded-md bg-slate-900 text-white" data-testid="cir-sub-save">Save subscription</button>
        </div>
      </div>
      <div className="bg-white border border-slate-200 rounded-md overflow-hidden" data-testid="cir-sub-dispatches">
        <div className="p-3 border-b border-slate-200 text-sm font-medium text-slate-900">Recent dispatches</div>
        {dispatches.length === 0 ? (
          <div className="p-6 text-sm text-slate-500">No dispatches yet. Click <b>Send now</b> or wait for the scheduled day.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase tracking-widest text-slate-500 bg-slate-50">
              <tr><th className="text-left py-2 px-3">Dispatched at</th><th className="text-left py-2 px-3">Trigger</th><th className="text-left py-2 px-3">Recipients</th><th className="text-left py-2 px-3">Status</th></tr>
            </thead>
            <tbody>
              {dispatches.map((d) => (
                <tr key={d.id} className="border-t border-slate-100">
                  <td className="py-2 px-3 text-slate-800">{d.dispatched_at?.slice(0, 16).replace("T", " ")}</td>
                  <td className="py-2 px-3 text-slate-700">{d.triggered_by}</td>
                  <td className="py-2 px-3 text-xs text-slate-600 font-mono">{(d.recipient_emails || []).join(", ")}</td>
                  <td className="py-2 px-3 text-xs text-amber-800">{d.delivery_status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

